import React, { useState, useRef, useEffect } from "react";
import { X, Send, Loader2, Wrench, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import Nina from "@/components/Nina";
import "@/components/nina.css";

// Base de conhecimento do AutoPost BR
const KB = [
  {
    tags: ["grupo", "adicionar", "cadastrar", "id", "como adiciono", "como coloco"],
    answer: `Para adicionar um grupo é simples! 👇
1. Abra o Facebook em outra aba e entre no grupo
2. Copie o número que aparece na URL após /groups/ (ex: facebook.com/groups/**123456789**)
3. Aqui na plataforma vá em **Grupos** no menu lateral
4. Cole o ID e clique em Adicionar

Pronto! O grupo já aparece para selecionar nas campanhas. 🎉`,
  },
  {
    tags: ["extensão", "extensao", "instalar", "chrome", "baixar", "plugin"],
    answer: `A extensão é o que faz a mágica acontecer! 🔌
1. Vá em **Extensão** no menu lateral
2. Baixe o arquivo ZIP da extensão
3. No Chrome, acesse **chrome://extensions**
4. Ative o **Modo do desenvolvedor** (canto superior direito)
5. Clique em **Carregar sem compactação** e selecione a pasta da extensão
6. Gere seu token na aba Extensão e cole no popup da extensão

Após conectar, ela automatiza tudo sozinha! 🚀`,
  },
  {
    tags: ["post", "campanha", "criar", "publicar", "postar", "divulgar", "como uso", "como funciona"],
    answer: `Criar uma campanha é rapidinho! ⚡
1. Vá no **Painel** (página inicial)
2. Escreva o texto do seu post
3. Adicione uma imagem se quiser
4. Marque os grupos que deseja atingir
5. Defina o delay (mínimo 60 segundos entre posts)
6. Clique em **Iniciar Divulgação**

A extensão abre cada grupo automaticamente e publica! 😎`,
  },
  {
    tags: ["delay", "intervalo", "tempo", "espera", "quantos segundos", "velocidade"],
    answer: `O delay é o tempo de espera entre cada postagem. ⏱️

Recomendamos **120 segundos** (2 minutos) para evitar bloqueios do Facebook. O mínimo permitido é 60 segundos.

Quanto mais espaçado, menor o risco de sua conta ser limitada pelo Facebook. Para contas novas, use 180s ou mais.`,
  },
  {
    tags: ["plano", "pro", "agência", "agencia", "grátis", "gratis", "preço", "preco", "assinar", "upgrade"],
    answer: `Temos 3 planos disponíveis! 💎

🆓 **Grátis** — Até 10 grupos e 50 disparos/mês
⚡ **Pro (R$49/mês)** — Até 200 grupos, 5.000 disparos, agendamento
👑 **Agência (R$149/mês)** — Ilimitado em tudo + multi-contas

Acesse **Planos** no menu para fazer o upgrade via Pix. O acesso é liberado em menos de 1 minuto após o pagamento!`,
  },
  {
    tags: ["pix", "pagamento", "pagar", "cobrado", "cobrança", "boleto"],
    answer: `Aceitamos pagamento via **Pix** — rápido e seguro! 💳

1. Vá em **Planos** no menu
2. Escolha o plano desejado
3. Clique em **Assinar via Pix**
4. Escaneie o QR Code ou copie o código copia-e-cola
5. Pague no seu banco normalmente

O acesso é liberado **automaticamente** em menos de 1 minuto após a confirmação! 🎉`,
  },
  {
    tags: ["agendamento", "agendar", "programar", "data", "hora", "futuro"],
    answer: `O agendamento está disponível nos planos **Pro** e **Agência**! 📅

Com ele você pode programar suas campanhas para disparar em uma data e hora específica — perfeito para postar no horário de maior engajamento do seu público.

Acesse a aba **Agendamento** no menu lateral para criar campanhas programadas.`,
  },
  {
    tags: ["erro", "falha", "não funciona", "nao funciona", "problema", "bug", "travou", "parou"],
    answer: `Vish, deu problema! Vamos resolver. 🔧

As causas mais comuns são:
• **Extensão desconectada** — abra o popup e verifique se está conectada
• **Facebook com captcha** — abra o Facebook manualmente e resolva
• **Conta nova** — aumente o delay para 180s ou use o modo **Semi-auto**
• **Grupo privado** — certifique-se de ser membro do grupo

Se persistir, tente o modo **Semi-auto** nas configurações da extensão — ele preenche tudo e você só clica em Publicar.`,
  },
  {
    tags: ["semi-auto", "semi auto", "manual", "automático", "automatico", "modo"],
    answer: `Temos dois modos de operação! ⚙️

🤖 **Automático** — A extensão faz tudo sozinha, do início ao fim. Ideal para contas estabelecidas.

👆 **Semi-auto** — A extensão abre o grupo e preenche o post, mas você clica em Publicar. Recomendado para contas novas ou após bloqueios.

Troque o modo no popup da extensão a qualquer momento.`,
  },
  {
    tags: ["facebook", "conta", "bloqueio", "bloqueado", "limitado", "restrito"],
    answer: `Para evitar bloqueios do Facebook! 🛡️

• Use delay de **120s ou mais** entre posts
• Prefira o modo **Semi-auto** para contas novas
• Não poste o mesmo conteúdo em grupos similares
• Varie o texto usando os botões **Random T/D/M** no painel
• Faça pausas — evite rodar 24h sem parar

Quanto mais natural parecer, menor o risco de limitação.`,
  },
  {
    tags: ["afiliado", "indicação", "indicacao", "comissão", "comissao", "ganhar"],
    answer: `Temos um programa de afiliados incrível! 💰

Você ganha **30% de comissão recorrente** por cada pessoa que assinar usando seu link de indicação!

Acesse **Afiliados** no menu para pegar seu link personalizado e acompanhar suas comissões. O pagamento é feito via Pix.`,
  },
  {
    tags: ["histórico", "historico", "relatório", "relatorio", "resultado", "enviados"],
    answer: `Você pode acompanhar todas as campanhas! 📊

Acesse **Histórico** no menu lateral para ver:
• Todas as campanhas realizadas
• Status de cada postagem (enviado/falha)
• Data e grupo de cada disparo

O histórico fica disponível por 7 dias no plano Grátis, 90 dias no Pro e 1 ano no Agência.`,
  },
];

const FALLBACK = `Hmm, não tenho certeza sobre isso! 🤔

Mas posso te ajudar com:
• Como adicionar grupos
• Como instalar a extensão
• Como criar campanhas
• Dúvidas sobre planos e pagamento
• Problemas e erros

Me conta mais detalhes que tentarei ajudar! 😊`;

// Devolve null quando a KB não tem resposta — o chamador decide o que fazer
// (perguntar pro backend, se logado, ou cair no FALLBACK).
function findAnswer(text) {
  const lower = text.toLowerCase();
  for (const item of KB) {
    if (item.tags.some((tag) => lower.includes(tag))) {
      return item.answer;
    }
  }
  return null;
}

// Perguntas sobre a CONTA do usuário (dados que só o backend sabe) devem ir
// direto pro assistente, sem passar pela KB — senão uma pergunta como "quantos
// leads eu tenho no meu funil?" casaria com a tag "grupo"/"lead" da KB e
// devolveria texto genérico em vez do número real.
const TERMOS_DE_CONTA = [
  "meu", "minha", "meus", "minhas", "eu tenho", "tenho",
  "funil", "pipeline", "lead", "leads", "prospec",
  "campanha", "meta ads", "anúncio", "anuncio", "gasto", "gastei",
  "agente", "piloto", "automático", "automatico",
  "hoje", "ontem", "essa semana", "este mês", "esse mes",
  "quantos", "quantas", "quanto",
];

function pareceSobreAConta(text) {
  const lower = text.toLowerCase();
  return TERMOS_DE_CONTA.some((t) => lower.includes(t));
}

// Renderiza markdown simples (**bold**, listas com •, numeradas)
function renderText(text) {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    // Bold
    const parts = line.split(/\*\*(.*?)\*\*/g);
    const rendered = parts.map((p, j) =>
      j % 2 === 1 ? <strong key={j}>{p}</strong> : p
    );
    return <span key={i}>{rendered}{i < lines.length - 1 && <br />}</span>;
  });
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  // Recolher o assistente pra ele não cobrir botões no canto da tela (ex: o
  // botão de enviar das Conversas internas). A preferência fica salva no
  // navegador — recolheu uma vez, continua recolhido até a pessoa reabrir.
  const [recolhido, setRecolhido] = useState(() => {
    try { return localStorage.getItem("ninaRecolhida") === "1"; } catch (e) { return false; }
  });
  const alternarRecolhido = () => {
    setOpen(false);
    setRecolhido((atual) => {
      const novo = !atual;
      try { localStorage.setItem("ninaRecolhida", novo ? "1" : "0"); } catch (e) { /* sem storage, segue */ }
      return novo;
    });
  };
  // Este componente só monta com usuário logado (ver AIAssistantLogado no App.js).
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Olá! 👋 Eu sou a Nina, sua assistente aqui no AutoPost BR.\n\nPosso **consultar sua conta** — funil, leads, agente e campanhas — e também tirar dúvidas sobre grupos, extensão e planos. O que você quer saber?",
      local: true,
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Sugestões rápidas: mistura consulta de dados reais com dúvida de uso
  const SUGGESTIONS = [
    "Como está meu funil hoje?",
    "Quantos leads o agente contatou?",
    "Como estão minhas campanhas?",
    "Como adiciono um grupo?",
  ];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const novas = [...messages, { role: "user", content: msg }];
    setMessages(novas);
    setInput("");
    setLoading(true);

    try {
      // ── 1. Perguntas sobre a conta vão direto pro backend ──────────────
      const sobreAConta = pareceSobreAConta(msg);

      // ── 2. Senão, a KB local responde na hora: instantânea e sem custo ──
      if (!sobreAConta) {
        const local = findAnswer(msg);
        if (local) {
          await new Promise((r) => setTimeout(r, 400));
          // local: true — respostas da KB são textos longos de ajuda; mandá-las
          // no histórico só inflaria o input do modelo sem agregar contexto.
          setMessages((m) => [
            ...m,
            { role: "assistant", content: local, local: true },
          ]);
          return;
        }
      }

      // ── 3. Assistente com acesso aos dados da conta ────────────────────
      const { data } = await api.post("/ai/chat", {
        message: msg,
        history: novas
          .filter((m) => !m.local)
          .slice(-10)
          .map(({ role, content }) => ({ role, content })),
      });

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.reply,
          tools: data.tools_used || [],
        },
      ]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      // Se o assistente falhar, ainda tenta a KB antes de desistir —
      // melhor uma resposta genérica que um erro seco.
      const local = findAnswer(msg);

      const aviso =
        status === 429
          ? detail || "Você atingiu o limite de perguntas por hoje. 😅"
          : status === 401
          ? "Sua sessão expirou. Faça login de novo pra eu consultar seus dados."
          : local
          ? local
          : "Não consegui consultar seus dados agora. Tenta de novo em instantes?";

      setMessages((m) => [...m, { role: "assistant", content: aviso }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const showSuggestions = messages.length <= 1;

  return (
    <>
      {/* Botão flutuante + aba na borda direita pra recolher/mostrar.
          A aba ocupa os 24px que já sobravam à direita do botão, então o
          gatinho continua exatamente no mesmo lugar de sempre. */}
      <div className="fixed bottom-6 right-0 z-[300] flex items-center">
        {!recolhido && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="nina-host h-14 w-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
          >
            {open ? <X className="h-6 w-6" /> : <Nina size={40} />}
          </button>
        )}
        <button
          onClick={alternarRecolhido}
          title={recolhido ? "Mostrar assistente" : "Esconder assistente"}
          aria-label={recolhido ? "Mostrar assistente" : "Esconder assistente"}
          className={`h-12 w-6 rounded-l-xl flex items-center justify-center shadow-lg transition-colors ${
            recolhido ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-zinc-700/80 hover:bg-zinc-600 text-white"
          }`}
        >
          {recolhido ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </div>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[300] w-80 sm:w-96 bg-[#ffffff] dark:bg-zinc-900 rounded-sm shadow-2xl border border-[#e4e4e7] dark:border-zinc-700 flex flex-col overflow-hidden"
          style={{ maxHeight: "min(520px, calc(100vh - 8rem))" }}
        >
          {/* Header */}
          <div className="bg-zinc-950 text-white px-4 py-3 flex items-center gap-3">
            <div className="h-8 w-8 bg-zinc-100 dark:bg-zinc-800 rounded-sm flex items-center justify-center overflow-hidden">
              <Nina size={26} state="happy" />
            </div>
            <div>
              <div className="font-semibold text-sm">Nina Assistant</div>
              <div className="text-xs text-zinc-400 flex items-center gap-1">
                <span className="h-1.5 w-1.5 bg-green-400 rounded-full inline-block" />
                Online
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50 dark:bg-zinc-950">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                {m.role === "assistant" && (
                  <div className="h-7 w-7 bg-zinc-100 dark:bg-zinc-800 rounded-sm flex-shrink-0 flex items-center justify-center overflow-hidden mt-0.5">
                    <Nina size={22} state="idle" />
                  </div>
                )}
                <div className="max-w-[78%]">
                  <div
                    className={`px-3 py-2 rounded-sm text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-blue-600 text-white"
                        : "bg-[#ffffff] dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 border border-[#e4e4e7] dark:border-zinc-700"
                    }`}
                  >
                    {renderText(m.content)}
                  </div>

                  {/* Transparência: o que foi consultado pra montar a resposta */}
                  {m.tools?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {m.tools.map((t, j) => (
                        <span
                          key={j}
                          className="inline-flex items-center gap-1 rounded-sm bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-500 dark:text-zinc-400"
                        >
                          <Wrench className="h-2.5 w-2.5" />
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2">
                <div className="h-7 w-7 bg-zinc-100 dark:bg-zinc-800 rounded-sm flex-shrink-0 flex items-center justify-center overflow-hidden">
                  <Nina size={22} state="idle" />
                </div>
                <div className="bg-[#ffffff] dark:bg-zinc-800 border border-[#e4e4e7] dark:border-zinc-700 rounded-sm px-3 py-2">
                  <Loader2 className="h-4 w-4 text-zinc-400 animate-spin" />
                </div>
              </div>
            )}

            {/* Sugestões rápidas */}
            {showSuggestions && !loading && (
              <div className="space-y-2 pt-1">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    className="w-full text-left text-xs px-3 py-2 bg-[#ffffff] dark:bg-zinc-800 border border-[#e4e4e7] dark:border-zinc-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-zinc-700 rounded-sm transition-colors text-zinc-700 dark:text-zinc-200"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-[#ffffff] dark:bg-zinc-900 border-t border-[#e4e4e7] dark:border-zinc-700 flex gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Digite sua dúvida..."
              className="flex-1 text-sm px-3 py-2 bg-[#ffffff] dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 border border-[#e4e4e7] dark:border-zinc-700 rounded-sm focus:outline-none focus:border-blue-500"
              disabled={loading}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="h-9 w-9 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-sm flex items-center justify-center transition-colors"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
